import { useContext, useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { yupResolver } from "@hookform/resolvers/yup"
import { useReusableWizard } from "@oysterhr/feature-components"
import { useParams } from "react-router-dom"
import { BankDetailsContext } from "../contexts/BankDetailsContext"
import { PaymentEngagementType } from "../../../../enums"
import { type AddAchFormValues } from "../../../types"
import { createAchFormValidationSchema } from "../utils/formHelpers"
import { useUpsertAchPaymentMethod } from "./useUpsertAchPaymentMethod"

const defaultBankDetails = {
  country: "US",
  currencyCode: "USD",
  routingNumber: "",
  accountNumber: "",
  accountHolderName: "",
  bankAccountType: undefined,
}

export const useAchPaymentMethodForm = ({ onSuccess }: { onSuccess: () => void }) => {
  // Hooks
  const { goToNext } = useReusableWizard()
  const { paymentFlow } = useParams()
  const { bankDetails, setBankDetails } = useContext(BankDetailsContext)
  const { t } = useTranslation("company_interface/AddStripeAchPage")

  const { upsertFTEPaymentMethod, createPaymentMethod, stripeReady, isLoading } =
    useUpsertAchPaymentMethod({ onSuccess })

  //Form
  const validationSchema = useMemo(() => createAchFormValidationSchema(t), [t])
  const form = useForm<AddAchFormValues>({
    defaultValues: bankDetails || defaultBankDetails,
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = async (values: AddAchFormValues) => {
    if (paymentFlow === PaymentEngagementType.Fte) {
      return upsertFTEPaymentMethod(values)
    } else {
      // NOTE: We delay creation of contractor payment method until last step
      // so we don't need update here.
      return createPaymentMethod(values)
    }
  }

  const onNext = (values: AddAchFormValues) => {
    setBankDetails(values)
    goToNext()
  }

  return {
    ...form,
    onSubmit,
    onNext,
    formIsReady: stripeReady,
    loading: isLoading,
  }
}
